import { Badge, Box, IconButton, Tooltip, Typography } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import Icon from 'components/Icon';
import { CollectedData } from 'types';

interface CollectedDataCellProps extends GridRenderCellParams {
  row: {
    collectedData?: CollectedData;
  };
}

const CollectedDataCell = ({ row }: CollectedDataCellProps) => {
  const { collectedData } = row;

  if (!collectedData) {
    return (
      <Typography variant='body2' color='text.secondary'>
        No data
      </Typography>
    );
  }

  // Flatten the collected data
  const flattenedData: Record<string, any> = {};

  // Add basic properties (excluding basic user info that's already shown in other columns)
  // Only include non-standard collected data
  if (collectedData.phoneNumber) {
    flattenedData.phoneNumber = collectedData.phoneNumber;
  }
  if (collectedData.telegramUserId) {
    flattenedData.telegramUserId = collectedData.telegramUserId;
  }

  // Flatten location data
  if (collectedData.location) {
    if (collectedData.location.latitude) {
      flattenedData.latitude = collectedData.location.latitude;
    }
    if (collectedData.location.longitude) {
      flattenedData.longitude = collectedData.location.longitude;
    }
    if (collectedData.location.address) {
      flattenedData.address = collectedData.location.address;
    }
  }

  // Flatten webApp data - extract inner objects directly
  if (collectedData.webApp) {
    Object.entries(collectedData.webApp).forEach(([key, value]) => {
      flattenedData[key] = value;
    });
  }

  // Flatten commands data - extract inner objects directly
  if (collectedData.commands) {
    Object.entries(collectedData.commands).forEach(([commandKey, commandValue]) => {
      if (typeof commandValue === 'object' && commandValue !== null) {
        // Extract the inner object properties directly (e.g., q1, q2, q3 from start command)
        Object.entries(commandValue as Record<string, any>).forEach(([key, value]) => {
          flattenedData[key] = value;
        });
      } else {
        flattenedData[commandKey] = commandValue;
      }
    });
  }

  const dataEntries = Object.entries(flattenedData);

  if (dataEntries.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center' }}>
        -
      </Typography>
    );
  }

  const allDataText = dataEntries.map(([key, value]) => `${key}: ${value}`).join('\n');

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Collected Data ({dataEntries.length} items):
          </Typography>
          <Typography variant='body2' component='pre' sx={{ whiteSpace: 'pre-wrap' }}>
            {allDataText}
          </Typography>
        </Box>
      }
      placement='top'
      arrow
    >
      <Badge
        badgeContent={dataEntries.length}
        color='primary'
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.75rem',
            minWidth: '16px',
            height: '16px',
            transform: 'scale(0.9) translate(20%, -20%)',
          },
        }}
      >
        <IconButton
          size='medium'
          sx={{
            color: 'primary.main',
            padding: '8px',
            '&:hover': {
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
            },
          }}
        >
          <Icon icon='data_object' fontSize='small' />
        </IconButton>
      </Badge>
    </Tooltip>
  );
};

export default CollectedDataCell;
